import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Search,
  Trash2,
  ArrowLeft,
  Edit3,
  Save,
  User,
  Key,
  LayoutDashboard,
  SettingsIcon,
  AlertCircle,
  Clock,
} from './Icons.jsx';
import { Toast, ConfirmModal, Badge } from './UI.jsx';
import { AddressAutocomplete } from './AddressAutocomplete.jsx';
import {
  apiGetFirmen,
  apiAddFirma,
  apiUpdateFirma,
  apiDeleteFirma,
  apiGetNutzer,
  apiAddNutzer,
  apiUpdateNutzer,
  apiDeleteNutzer,
  apiAdminOverview,
  apiAdminGetCompanyConfig,
  apiAdminSaveCompanyConfig,
  apiAdminGetAudit,
  apiAdminMaintenanceStatus,
  apiAdminMaintenanceAction,
  apiAdminMonitoring,
} from '../api/ionos.js';

const PLAN_LABELS = {
  starter: 'Starter',
  growth: 'Growth (Neues Limit)',
  business: 'Business',
};

export default function AdminPanel() {
  function handleLogout() {
    localStorage.removeItem('devsuite_user');
    localStorage.removeItem('devsuite_company_id');
    window.location.reload();
  }

  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const [firmen, setFirmen] = useState([]);
  const [nutzer, setNutzer] = useState([]);

  const [viewCompanyId, setViewCompanyId] = useState(null);
  const [companySearch, setCompanySearch] = useState('');
  const [adminTab, setAdminTab] = useState('dashboard');
  const [detailTab, setDetailTab] = useState('overview');

  const [showFirmaForm, setShowFirmaForm] = useState(false);
  const [isEditingFirma, setIsEditingFirma] = useState(false);
  const [formFirma, setFormFirma] = useState({});

  const [showNutzerForm, setShowNutzerForm] = useState(false);
  const [isEditingNutzer, setIsEditingNutzer] = useState(false);
  const [formNutzer, setFormNutzer] = useState({ email: '', password: '', company_id: null, is_admin: 0 });

  const [adminOverview, setAdminOverview] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  const [maintenanceStatus, setMaintenanceStatus] = useState(null);
  const [monitoring, setMonitoring] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);
  const [configSaving, setConfigSaving] = useState(false);
  const [loadingAdminData, setLoadingAdminData] = useState(false);

  const currentCompany = useMemo(() => firmen.find((f) => Number(f.id) === Number(viewCompanyId)), [firmen, viewCompanyId]);
  const currentOverviewCompany = useMemo(() => {
    const list = Array.isArray(adminOverview?.companies) ? adminOverview.companies : [];
    return list.find((c) => Number(c.id) === Number(viewCompanyId)) || null;
  }, [adminOverview, viewCompanyId]);

  const filteredFirmen = useMemo(() => {
    const q = companySearch.toLowerCase();
    return firmen.filter((f) => (f.name || '').toLowerCase().includes(q) || (f.email || '').toLowerCase().includes(q));
  }, [firmen, companySearch]);

  const reloadMasterData = async () => {
    const [f, n] = await Promise.all([apiGetFirmen(), apiGetNutzer()]);
    setFirmen(Array.isArray(f) ? f : []);
    setNutzer(Array.isArray(n) ? n : []);
  };

  const reloadAdminData = async (companyId = null) => {
    setLoadingAdminData(true);
    try {
      const [overview, audit, maintenance, monitor] = await Promise.all([
        apiAdminOverview(),
        apiAdminGetAudit(120, companyId),
        apiAdminMaintenanceStatus(),
        apiAdminMonitoring(),
      ]);
      setAdminOverview(overview || null);
      setAuditRows(Array.isArray(audit) ? audit : []);
      setMaintenanceStatus(maintenance || null);
      setMonitoring(monitor || null);
    } catch (e) {
      setToast({ message: 'Admin-Daten konnten nicht geladen werden: ' + (e.message || e), type: 'error' });
    } finally {
      setLoadingAdminData(false);
    }
  };

  const reloadAll = async (companyId = null) => {
    try {
      await reloadMasterData();
      await reloadAdminData(companyId);
    } catch (e) {
      setToast({ message: 'Fehler beim Laden: ' + (e.message || e), type: 'error' });
    }
  };

  useEffect(() => {
    reloadAll();
  }, []);

  useEffect(() => {
    if (!viewCompanyId) {
      setCompanyConfig(null);
      return;
    }
    (async () => {
      try {
        const cfg = await apiAdminGetCompanyConfig(viewCompanyId);
        setCompanyConfig(cfg || null);
        await reloadAdminData(viewCompanyId);
      } catch (e) {
        setToast({ message: 'Konfiguration konnte nicht geladen werden: ' + (e.message || e), type: 'error' });
      }
    })();
  }, [viewCompanyId]);

  const handleSaveCompany = async () => {
    try {
      if (isEditingFirma) {
        await apiUpdateFirma(formFirma);
        setToast({ message: 'Firma aktualisiert', type: 'success' });
      } else {
        await apiAddFirma(formFirma);
        setToast({ message: 'Firma erstellt', type: 'success' });
      }
      setShowFirmaForm(false);
      await reloadAll(viewCompanyId || null);
    } catch (e) {
      setToast({ message: e.message || 'Fehler beim Speichern', type: 'error' });
    }
  };

  const handleSaveUser = async () => {
    try {
      if (isEditingNutzer) {
        const payload = { ...formNutzer, company_id: viewCompanyId };
        if (!payload.password) delete payload.password;
        await apiUpdateNutzer(payload);
        setToast({ message: 'Benutzer aktualisiert', type: 'success' });
      } else {
        await apiAddNutzer({ ...formNutzer, company_id: viewCompanyId });
        setToast({ message: 'Benutzer angelegt', type: 'success' });
      }
      setShowNutzerForm(false);
      await reloadAll(viewCompanyId || null);
    } catch (e) {
      setToast({ message: e.message || 'Fehler beim Speichern', type: 'error' });
    }
  };

  const handleSaveConfig = async () => {
    if (!viewCompanyId || !companyConfig) return;
    setConfigSaving(true);
    try {
      const saved = await apiAdminSaveCompanyConfig({
        company_id: viewCompanyId,
        plan: companyConfig.plan,
        limits: companyConfig.limits,
        features: companyConfig.features,
      });
      setCompanyConfig({ ...companyConfig, ...saved });
      setToast({ message: 'Feature-Flags und Limits gespeichert', type: 'success' });
      await reloadAdminData(viewCompanyId);
    } catch (e) {
      setToast({ message: e.message || 'Konfiguration konnte nicht gespeichert werden', type: 'error' });
    } finally {
      setConfigSaving(false);
    }
  };

  const runMaintenance = async (action, successMsg, payload = {}) => {
    try {
      await apiAdminMaintenanceAction(action, payload);
      setToast({ message: successMsg, type: 'success' });
      await reloadAdminData(viewCompanyId || null);
    } catch (e) {
      setToast({ message: e.message || 'Maintenance-Aktion fehlgeschlagen', type: 'error' });
    }
  };

  const renderDashboardTab = () => {
    const totals = adminOverview?.totals || { companies: 0, users: 0, admins: 0 };
    const companies = Array.isArray(adminOverview?.companies) ? adminOverview.companies : [];

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="nb-card p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500">Firmen</p>
            <p className="text-2xl font-extrabold text-slate-900">{totals.companies}</p>
          </div>
          <div className="nb-card p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500">Benutzer</p>
            <p className="text-2xl font-extrabold text-slate-900">{totals.users}</p>
          </div>
          <div className="nb-card p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500">Admins</p>
            <p className="text-2xl font-extrabold text-slate-900">{totals.admins}</p>
          </div>
        </div>

        <div className="space-y-3">
          {companies.map((c) => (
            <div key={c.id} className="nb-card p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-semibold text-slate-900">{c.name}</p>
                  <p className="text-xs text-slate-500">Plan: {PLAN_LABELS[c.plan] || c.plan}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${c.health?.status === 'warning' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {c.health?.status === 'warning' ? 'Warnung' : 'OK'}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setViewCompanyId(c.id); setDetailTab('overview'); }}
                    className="px-3 py-1.5 rounded-full bg-[#007AFF] text-white text-xs font-semibold"
                  >
                    Öffnen
                  </button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-slate-600">
                <div>Kunden: <span className="font-semibold">{c.usage?.customers || 0}</span></div>
                <div>Rechnungen: <span className="font-semibold">{c.usage?.invoices || 0}</span></div>
                <div>Offene Tasks: <span className="font-semibold">{c.usage?.openTasks || 0}</span></div>
                <div>Benutzer: <span className="font-semibold">{c.usage?.users || 0}</span></div>
              </div>
              {Array.isArray(c.warnings) && c.warnings.length > 0 && (
                <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Limits fast erreicht: {c.warnings.map((w) => `${w.key} (${w.used}/${w.limit})`).join(', ')}
                </div>
              )}
            </div>
          ))}
          {companies.length === 0 && <div className="nb-card p-6 text-slate-500 text-center">Keine Firmendaten verfügbar.</div>}
        </div>
      </div>
    );
  };

  const renderCompaniesTab = () => (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Firmen suchen..."
            value={companySearch}
            onChange={(e) => setCompanySearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
        <button
          onClick={() => {
            setFormFirma({ name: '', info: '', adresse: '', plz: '', ort: '', land: '', email: '', telefon: '', ansprechpartner: '' });
            setIsEditingFirma(false);
            setShowFirmaForm(true);
          }}
          className="flex items-center justify-center px-6 py-2.5 min-h-[44px] bg-[#007AFF] text-white rounded-full hover:opacity-90 active:scale-[0.98] shadow-sm font-semibold"
        >
          <Plus size={20} className="mr-2" /> Neue Firma
        </button>
      </div>

      <div className="bg-white rounded-[20px] shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[700px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Firma</th>
                <th className="p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Kontakt</th>
                <th className="p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Standort</th>
                <th className="p-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredFirmen.length === 0 ? (
                <tr><td colSpan={4} className="p-8 text-center text-slate-500">Keine Firmen gefunden.</td></tr>
              ) : (
                filteredFirmen.map((firma) => (
                  <tr key={firma.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => { setViewCompanyId(firma.id); setDetailTab('overview'); }}>
                    <td className="p-4">
                      <div className="font-medium text-slate-900">{firma.name}</div>
                      {firma.info && <div className="text-xs text-slate-500 mt-1 max-w-[260px] truncate">{firma.info}</div>}
                    </td>
                    <td className="p-4">
                      {firma.ansprechpartner && <div className="text-sm text-slate-800">{firma.ansprechpartner}</div>}
                      <div className="text-xs text-slate-500">{firma.email}</div>
                      <div className="text-xs text-slate-500">{firma.telefon}</div>
                    </td>
                    <td className="p-4 text-sm text-slate-600">
                      <div>{firma.plz} {firma.ort}</div>
                      {firma.land && <div className="text-xs text-slate-400">{firma.land}</div>}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormFirma({ ...firma });
                            setIsEditingFirma(true);
                            setShowFirmaForm(true);
                          }}
                          className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                          title="Bearbeiten"
                        >
                          <Edit3 size={18} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirm({
                              message: `Firma "${firma.name}" und alle zugehörigen Daten wirklich löschen?`,
                              confirmLabel: 'Endgültig löschen',
                              onConfirm: async () => {
                                await apiDeleteFirma(firma.id);
                                setConfirm(null);
                                await reloadAll();
                                setToast({ message: 'Firma gelöscht', type: 'success' });
                              },
                              onCancel: () => setConfirm(null),
                            });
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Löschen"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderAuditTab = () => (
    <div className="nb-card p-4 sm:p-6">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="text-lg font-semibold text-slate-800">Audit-Log</h3>
        <button type="button" className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold" onClick={() => reloadAdminData(viewCompanyId || null)}>Aktualisieren</button>
      </div>
      <div className="space-y-2 max-h-[520px] overflow-auto">
        {auditRows.map((row) => (
          <div key={row.id} className="rounded-xl border border-slate-200 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-800">{row.action}</p>
              <p className="text-xs text-slate-500">{row.created_at}</p>
            </div>
            <p className="text-xs text-slate-500 mt-1">Admin #{row.admin_user_id} · {row.target_type || '-'} {row.target_id || ''}</p>
          </div>
        ))}
        {auditRows.length === 0 && <p className="text-sm text-slate-500">Keine Audit-Einträge.</p>}
      </div>
    </div>
  );

  const renderMaintenanceTab = () => (
    <div className="space-y-4">
      <div className="nb-card p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">Wartungscenter</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button type="button" onClick={() => runMaintenance('health_check', 'Health-Check ausgeführt')} className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-semibold">Health-Check</button>
          <button type="button" onClick={() => runMaintenance('run_migration', 'Migration ausgeführt')} className="px-4 py-2 rounded-xl bg-blue-100 hover:bg-blue-200 text-sm font-semibold text-blue-700">Migration ausführen</button>
          <button type="button" onClick={() => runMaintenance('clear_php_error_log', 'PHP-Error-Log geleert')} className="px-4 py-2 rounded-xl bg-amber-100 hover:bg-amber-200 text-sm font-semibold text-amber-700">Error-Log leeren</button>
        </div>
      </div>
      <div className="nb-card p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-2">Einmaliger Nummern-Reset</h3>
        <p className="text-sm text-slate-600 mb-4">
          Setzt alle Belegnummern systemweit zurück (Neuanfang) und kann nur einmal ausgeführt werden.
        </p>
        <button
          type="button"
          disabled={!!maintenanceStatus?.document_number_reset?.done}
          onClick={() => {
            setConfirm({
              message: 'ACHTUNG: Alle Belegnummern (Rechnungen/Gutschriften/Stornos/Angebote) werden einmalig neu vergeben. Fortfahren?',
              confirmLabel: 'Reset jetzt starten',
              onConfirm: async () => {
                setConfirm(null);
                await runMaintenance('reset_document_numbers_once', 'Dokumentnummern wurden zurückgesetzt.');
              },
              onCancel: () => setConfirm(null),
            });
          }}
          className="px-4 py-2 rounded-xl bg-red-100 hover:bg-red-200 text-sm font-semibold text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Nummern einmalig reseten
        </button>
        {maintenanceStatus?.document_number_reset?.done && (
          <p className="text-xs text-emerald-700 mt-2">
            Bereits ausgeführt: {maintenanceStatus?.document_number_reset?.marker_mtime || 'unbekannt'}
          </p>
        )}
      </div>
      <div className="nb-card p-4 sm:p-6 text-sm text-slate-600">
        <h4 className="font-semibold text-slate-800 mb-2">Systemstatus</h4>
        <pre className="text-xs bg-slate-50 rounded-lg p-3 overflow-auto">{JSON.stringify(maintenanceStatus || {}, null, 2)}</pre>
      </div>
    </div>
  );

  const renderMonitoringTab = () => (
    <div className="space-y-4">
      <div className="nb-card p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">Fehler-Monitoring</h3>
        <p className="text-sm text-slate-500 mb-2">PHP-Errors (letzte Einträge)</p>
        <div className="max-h-[220px] overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-3">
          {(monitoring?.php_errors || []).slice(-60).map((line, idx) => (
            <p key={idx} className="text-xs text-slate-700 break-words">{line}</p>
          ))}
          {(!monitoring?.php_errors || monitoring.php_errors.length === 0) && <p className="text-xs text-slate-500">Keine PHP-Fehler protokolliert.</p>}
        </div>
      </div>
      <div className="nb-card p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">Mail-Monitoring</h3>
        <div className="space-y-2 max-h-[280px] overflow-auto">
          {(monitoring?.email_logs || []).slice(0, 50).map((m) => (
            <div key={m.id} className="rounded-lg border border-slate-200 p-2 text-xs">
              <p className="font-semibold text-slate-700">{m.document_type} #{m.document_id}</p>
              <p className="text-slate-500">{m.to_email} · {m.sent_at}</p>
              <p className="text-slate-600 break-words">{m.subject}</p>
            </div>
          ))}
          {(!monitoring?.email_logs || monitoring.email_logs.length === 0) && <p className="text-sm text-slate-500">Keine Mail-Logs verfügbar.</p>}
        </div>
      </div>
    </div>
  );

  const renderCompanyDetail = () => {
    const companyUsers = nutzer.filter((u) => Number(u.company_id) === Number(viewCompanyId));

    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => { setViewCompanyId(null); setDetailTab('overview'); }}
            className="p-2.5 min-h-[44px] rounded-xl border border-slate-200 bg-white hover:bg-slate-50 flex items-center gap-2 text-slate-700"
          >
            <ArrowLeft size={20} /> Zurück
          </button>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800">{currentCompany?.name || 'Firma'}</h2>
        </div>

        <div className="flex gap-2 border-b border-slate-100 pb-2 overflow-x-auto">
          {[
            ['overview', 'Übersicht'],
            ['users', 'Benutzer'],
            ['config', 'Flags & Limits'],
            ['usage', 'Usage & Plan'],
            ['audit', 'Audit'],
            ['maintenance', 'Wartung'],
            ['monitoring', 'Monitoring'],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setDetailTab(id)}
              className={`px-4 py-2 rounded-full text-[13px] font-semibold whitespace-nowrap ${detailTab === id ? 'bg-[#007AFF] text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {detailTab === 'overview' && currentCompany && (
          <div className="nb-card p-6">
            <div className="flex justify-between items-start gap-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Firmenname</label>
                  <p className="text-lg font-medium text-slate-800">{currentCompany.name}</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Info / Notiz</label>
                  <p className="text-slate-600">{currentCompany.info || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Kontakt</label>
                  <p className="text-slate-800">{currentCompany.ansprechpartner || '-'}</p>
                  <p className="text-slate-600 text-sm">{currentCompany.email || '-'}</p>
                  <p className="text-slate-600 text-sm">{currentCompany.telefon || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Adresse</label>
                  <p className="text-slate-600 whitespace-pre-line">{currentCompany.adresse || '-'}\n{currentCompany.plz || ''} {currentCompany.ort || ''}\n{currentCompany.land || ''}</p>
                </div>
              </div>
              <button
                onClick={() => { setFormFirma({ ...currentCompany }); setIsEditingFirma(true); setShowFirmaForm(true); }}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                title="Firma bearbeiten"
              >
                <Edit3 size={20} />
              </button>
            </div>
          </div>
        )}

        {detailTab === 'users' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-slate-700">Benutzerkonten</h3>
              <button
                onClick={() => { setFormNutzer({ email: '', password: '', company_id: viewCompanyId, is_admin: 0 }); setIsEditingNutzer(false); setShowNutzerForm(true); }}
                className="px-5 py-2.5 min-h-[44px] bg-[#007AFF] text-white rounded-full hover:opacity-90 active:scale-[0.98] text-[15px] font-semibold shadow-sm flex items-center justify-center gap-2"
              >
                <Plus size={18} /> Neuer Benutzer
              </button>
            </div>
            <div className="nb-card overflow-hidden">
              <ul className="divide-y divide-slate-100">
                {companyUsers.length === 0 ? (
                  <li className="p-6 text-center text-slate-500">Keine Benutzer angelegt.</li>
                ) : (
                  companyUsers.map((usr) => (
                    <li key={usr.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><User size={20} /></div>
                        <div>
                          <p className="font-medium text-slate-800">{usr.email}</p>
                          <p className="text-xs text-slate-500">ID: {usr.id} {usr.is_admin ? '· Admin' : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => { setFormNutzer({ ...usr, password: '' }); setIsEditingNutzer(true); setShowNutzerForm(true); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Bearbeiten"><Edit3 size={18} /></button>
                        <button onClick={() => setConfirm({
                          message: `Benutzer ${usr.email} wirklich löschen?`,
                          confirmLabel: 'Löschen',
                          onConfirm: async () => {
                            await apiDeleteNutzer(usr.id);
                            setConfirm(null);
                            await reloadAll(viewCompanyId);
                            setToast({ message: 'Benutzer gelöscht', type: 'success' });
                          },
                          onCancel: () => setConfirm(null),
                        })} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Löschen"><Trash2 size={18} /></button>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        )}

        {detailTab === 'config' && (
          <div className="nb-card p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Plan</label>
                <select
                  className="w-full p-3 border border-slate-300 rounded-xl bg-white"
                  value={companyConfig?.plan || 'growth'}
                  onChange={(e) => {
                    const plan = e.target.value;
                    const presets = companyConfig?.presets || {};
                    setCompanyConfig((prev) => ({
                      ...prev,
                      plan,
                      limits: { ...(presets[plan] || prev?.limits || {}) },
                    }));
                  }}
                >
                  {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="text-xs text-slate-500 flex items-end">
                Neues Limit-Modell aktiv: Plan + individuelle Limits pro Firma.
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-slate-800 mb-2">Feature-Flags</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(companyConfig?.features || {}).map(([key, val]) => (
                  <label key={key} className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                    <input
                      type="checkbox"
                      checked={!!val}
                      onChange={(e) => setCompanyConfig((prev) => ({ ...prev, features: { ...(prev?.features || {}), [key]: e.target.checked } }))}
                    />
                    <span>{key}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-slate-800 mb-2">Limits</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(companyConfig?.limits || {}).map(([key, val]) => (
                  <div key={key}>
                    <label className="block text-xs text-slate-500 mb-1">{key}</label>
                    <input
                      type="number"
                      min="0"
                      className="w-full p-2.5 border border-slate-300 rounded-lg"
                      value={val}
                      onChange={(e) => setCompanyConfig((prev) => ({ ...prev, limits: { ...(prev?.limits || {}), [key]: Number(e.target.value || 0) } }))}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <button type="button" onClick={handleSaveConfig} disabled={configSaving} className="px-5 py-2.5 rounded-full bg-[#007AFF] text-white font-semibold disabled:opacity-50 flex items-center gap-2">
                <Save size={16} /> {configSaving ? 'Speichern…' : 'Speichern'}
              </button>
            </div>
          </div>
        )}

        {detailTab === 'usage' && (
          <div className="nb-card p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Usage & Plan</h3>
            {currentOverviewCompany ? (
              <div className="space-y-2 text-sm">
                {['customers', 'invoices', 'quotes', 'subscriptions', 'users', 'storageMb'].map((k) => {
                  const used = Number(currentOverviewCompany?.usage?.[k] || 0);
                  const limit = Number(currentOverviewCompany?.limits?.[k] || 0);
                  const pct = limit > 0 ? Math.round((used / limit) * 100) : 0;
                  return (
                    <div key={k} className="grid grid-cols-[120px_1fr_auto] gap-2 items-center">
                      <span className="text-slate-600">{k}</span>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${pct >= 90 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                      <span className="text-xs font-medium text-slate-700">{used}/{limit || '∞'}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Keine Usage-Daten gefunden.</p>
            )}
          </div>
        )}

        {detailTab === 'audit' && renderAuditTab()}
        {detailTab === 'maintenance' && renderMaintenanceTab()}
        {detailTab === 'monitoring' && renderMonitoringTab()}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col items-center py-6 sm:py-10 px-4">
      <div className="w-full max-w-6xl space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-[22px] sm:text-[28px] font-bold text-slate-900 tracking-tight">NextBill Admin</h1>
          <button onClick={handleLogout} className="text-[#FF3B30] hover:bg-[#FF3B30]/10 px-4 py-2 min-h-[44px] rounded-full font-medium transition-colors">Logout</button>
        </div>

        {!viewCompanyId && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {[
              ['dashboard', 'Dashboard', LayoutDashboard],
              ['companies', 'Firmen', User],
              ['audit', 'Audit', Clock],
              ['maintenance', 'Wartung', SettingsIcon],
              ['monitoring', 'Monitoring', AlertCircle],
            ].map(([id, label, IconComp]) => (
              <button
                key={id}
                type="button"
                onClick={() => setAdminTab(id)}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap flex items-center gap-2 ${adminTab === id ? 'bg-[#007AFF] text-white' : 'bg-white text-slate-700 border border-slate-200'}`}
              >
                <IconComp size={16} /> {label}
              </button>
            ))}
            <button type="button" onClick={() => reloadAll()} className="px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap bg-slate-100 text-slate-700 border border-slate-200">Refresh</button>
          </div>
        )}

        {loadingAdminData && <div className="text-sm text-slate-500">Lade Admin-Daten…</div>}

        {viewCompanyId ? (
          renderCompanyDetail()
        ) : (
          <>
            {adminTab === 'dashboard' && renderDashboardTab()}
            {adminTab === 'companies' && renderCompaniesTab()}
            {adminTab === 'audit' && renderAuditTab()}
            {adminTab === 'maintenance' && renderMaintenanceTab()}
            {adminTab === 'monitoring' && renderMonitoringTab()}
          </>
        )}
      </div>

      {showFirmaForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowFirmaForm(false)}>
          <div className="bg-white rounded-[24px] shadow-[0_8px_32px_rgba(0,0,0,0.12)] w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100 flex-shrink-0">
              <h3 className="text-[20px] font-bold text-slate-900 tracking-tight">{isEditingFirma ? 'Firma bearbeiten' : 'Neue Firma anlegen'}</h3>
            </div>
            <div className="p-6 overflow-y-auto space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Firmenname</label>
                <input className="w-full p-3 border border-slate-200 bg-slate-50 rounded-xl" value={formFirma.name || ''} onChange={(e) => setFormFirma({ ...formFirma, name: e.target.value })} autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Anschrift</label>
                <AddressAutocomplete
                  className="mb-2"
                  onSelect={(addr) => setFormFirma((prev) => ({ ...prev, adresse: addr.street, plz: addr.zip, ort: addr.city, land: prev.land || 'Deutschland' }))}
                />
                <input className="w-full p-3 border border-slate-200 bg-slate-50 rounded-xl mb-2" placeholder="Straße & Hausnr." value={formFirma.adresse || ''} onChange={(e) => setFormFirma({ ...formFirma, adresse: e.target.value })} />
                <div className="grid grid-cols-3 gap-2">
                  <input className="col-span-1 w-full p-3 border border-slate-200 bg-slate-50 rounded-xl" placeholder="PLZ" value={formFirma.plz || ''} onChange={(e) => setFormFirma({ ...formFirma, plz: e.target.value })} />
                  <input className="col-span-2 w-full p-3 border border-slate-200 bg-slate-50 rounded-xl" placeholder="Ort" value={formFirma.ort || ''} onChange={(e) => setFormFirma({ ...formFirma, ort: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input className="w-full p-3 border border-slate-200 bg-slate-50 rounded-xl" placeholder="Land" value={formFirma.land || ''} onChange={(e) => setFormFirma({ ...formFirma, land: e.target.value })} />
                <input className="w-full p-3 border border-slate-200 bg-slate-50 rounded-xl" placeholder="E-Mail" value={formFirma.email || ''} onChange={(e) => setFormFirma({ ...formFirma, email: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input className="w-full p-3 border border-slate-200 bg-slate-50 rounded-xl" placeholder="Telefon" value={formFirma.telefon || ''} onChange={(e) => setFormFirma({ ...formFirma, telefon: e.target.value })} />
                <input className="w-full p-3 border border-slate-200 bg-slate-50 rounded-xl" placeholder="Ansprechpartner" value={formFirma.ansprechpartner || ''} onChange={(e) => setFormFirma({ ...formFirma, ansprechpartner: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Interner Vermerk</label>
                <textarea className="w-full p-3 border border-slate-200 bg-slate-50 rounded-xl" rows={2} placeholder="Notiz..." value={formFirma.info || ''} onChange={(e) => setFormFirma({ ...formFirma, info: e.target.value })} />
              </div>
            </div>
            <div className="p-4 sm:p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3 flex-shrink-0">
              <button className="px-5 py-2.5 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-100 font-semibold" onClick={() => setShowFirmaForm(false)}>Abbrechen</button>
              <button className="px-5 py-2.5 rounded-full bg-[#007AFF] text-white hover:opacity-90 active:scale-[0.98] font-semibold shadow-sm" onClick={handleSaveCompany}>Speichern</button>
            </div>
          </div>
        </div>
      )}

      {showNutzerForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowNutzerForm(false)}>
          <div className="bg-white rounded-[24px] shadow-[0_8px_32px_rgba(0,0,0,0.12)] w-full max-w-sm overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100">
              <h3 className="text-[20px] font-bold text-slate-900 tracking-tight">{isEditingNutzer ? 'Benutzer bearbeiten' : 'Neuen Benutzer anlegen'}</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">E-Mail</label>
                <input className="w-full p-3 border border-slate-200 bg-slate-50 rounded-xl" placeholder="user@company.com" value={formNutzer.email || ''} onChange={(e) => setFormNutzer({ ...formNutzer, email: e.target.value })} autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Passwort {isEditingNutzer && <span className="text-xs text-slate-400">(leer lassen = unverändert)</span>}</label>
                <div className="relative">
                  <Key size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="password" className="w-full pl-10 p-3 border border-slate-200 bg-slate-50 rounded-xl" placeholder={isEditingNutzer ? 'Neues Passwort' : 'Passwort'} value={formNutzer.password || ''} onChange={(e) => setFormNutzer({ ...formNutzer, password: e.target.value })} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!formNutzer.is_admin} onChange={(e) => setFormNutzer({ ...formNutzer, is_admin: e.target.checked ? 1 : 0 })} />
                Als Admin anlegen
              </label>
            </div>
            <div className="p-4 sm:p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button className="px-5 py-2.5 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-100 font-semibold" onClick={() => setShowNutzerForm(false)}>Abbrechen</button>
              <button className="px-5 py-2.5 rounded-full bg-[#007AFF] text-white hover:opacity-90 active:scale-[0.98] font-semibold shadow-sm" onClick={handleSaveUser}>Speichern</button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />
      <ConfirmModal
        isOpen={!!confirm}
        message={confirm?.message}
        onConfirm={() => confirm?.onConfirm?.()}
        onCancel={() => confirm?.onCancel?.()}
        confirmLabel={confirm?.confirmLabel}
      />
    </div>
  );
}
